/*
 * @format
 *
 * This is a simplified form of the pending reducer
 * Reported state is stored separately.
 * Users desired changes are stored here when the user makes a change.
 * The selector picks the pending state, if that doesn't exist it uses the real one
 *
 * When we receive an rejection or timeout
 * - We pop it into the error (and the ui will pop a modal)
 *
 *
 */

import * as actionTypes from '../action-types';

/*
const examle = {
  requests: [
    {
      zoneDeviceId: 'prt-123',
      endpointId: 0,
      requestId: 'abcd-efgh-1234-5678',
      request: {
        setpoint: 2000,
      },
    },
  ],
  errors: [
    {
      zoneDeviceId: 'prt-123',
      endpointId: 0,
      requestId: 'abcd-efgh-1234-5678',
      message: 'There was an error',
    },
  ],
};
*/

// const stateExample = {
//   current: {
//     request: {
//       settingName: 'setZoneName',
//       requestId: 'uuid',

//       homeId: '4f...',
//       deviceId: 'dbsad23',
//       endpointId: 5,
//       value: 'new name',
//     },
//     receivedComplete: true,
//     receivedMatchingState: false,
//   },
//   error: {
//     zoneDeviceId: 'prt-123',
//     endpointId: 0,
//     requestId: 'abcd-efgh-1234-5678',
//     message: 'There was an error',
//   },
// };

export const initalState = {
  request: null,
  error: null,
};

function buildZoneSettingName(commandName) {
  // short cut for non-standard names
  if (
    commandName === 'hpiSetIdentifyMode' ||
    commandName === 'setIdentifyMode'
  ) {
    return 'identifyModeActive';
  }
  if (
    commandName === 'hpiSetZoneName' ||
    commandName === 'setZoneName' ||
    commandName === 'setCustomPrtProperty'
  ) {
    return 'friendlyName';
  }
  if (commandName === 'hpiSetPrtComfortTemperature') {
    return 'comfortTemperatureSetpoint';
  }
  if (commandName === 'hpiSetPrtReducedTemperature') {
    // typo is all the way back to the spec
    return 'reducdedTemperatureSetpoint';
  }

  let name = commandName;
  let lowerName = commandName.toLowerCase();

  // remove leading hpi
  // remove leading set
  // make first character lower case
  const hpiIndex = lowerName.indexOf('hpi');
  if (hpiIndex === 0) {
    name = name.substring(hpiIndex + 3);
    lowerName = lowerName.substring(hpiIndex + 3);
  }

  const setIndex = lowerName.indexOf('set');
  if (setIndex === 0) {
    name = name.substring(setIndex + 3);
    lowerName = lowerName.substring(setIndex + 3);
  }

  return name[0].toLowerCase() + name.substring(1);
}

function prtSuccessMatcher(request, liveMessage) {
  // special cases:
  if (
    request.prtSetting === 'unpairDevice' ||
    request.prtSetting === 'hpiUnpairDevice'
  ) {
    // need to check that the live message doesn't have the prt anymore
    const stillExists =
      liveMessage.zones.find(z => z.zoneId[0] === request.zoneId[0]) ||
      liveMessage.zigbeeRepeaters.find(z => z.deviceId === request.zoneId[0]);

    return !stillExists;
  }

  // timedDHW applies to the DHW zone, but is reported by the CH zone :facepalm:
  if (request.prtSetting === 'hpiTimedDhw') {
    const chZone = liveMessage.zones.find(
      z => z.zoneId[0] === request.zoneId[0] && z.zoneId[1] === 5,
    );

    return chZone?.timedDhw === request.value;
  }

  // dhwEmergencyOperation is reported on the hpi interface, not on a zone
  if (request.prtSetting === 'hpiSetDhwEmergencyOperation') {
    return liveMessage.hpi?.dhwEmergencyOperation === request.value;
  }

  if (request.prtSetting === 'timeFormat') {
    const zone = liveMessage.zones.find(
      z =>
        z.zoneId[0] === request.zoneId[0] && z.zoneId[1] === request.zoneId[1],
    );

    // SI Time Format is 0/1, HPI Time Format is true/false, request is true/false :facepalm:
    const match =
      (zone.timeFormat === 1 || zone.timeFormat === true) === request.value;

    return match;
  }

  // end of special cases
  // start of generic lookups

  const zoneSetting = buildZoneSettingName(request.prtSetting);

  if (zoneSetting) {
    const zone = liveMessage.zones.find(
      z =>
        z.zoneId[0] === request.zoneId[0] && z.zoneId[1] === request.zoneId[1],
    );

    if (zone[zoneSetting] === undefined) {
      console.log(
        'WARNING - Setting not in Live message when checking in settings-reducer',
        zoneSetting,
        request,
        zone,
      );
    }

    return zone[zoneSetting] === request.value;
  } else {
    console.log('WARNING - Unrecognised setting in settings-reducer', request);
  }

  return false;
}

function interfaceSuccessMatcher(request, liveMessage) {
  const inf = liveMessage.si || liveMessage.hpi;

  if (
    request.settingName === 'unpairDevice' ||
    request.settingName === 'hpiUnpairDevice'
  ) {
    // need to check that the live message doesn't have the prt anymore
    const stillExists =
      liveMessage.zones.find(z => z.zoneId[0] === request.value) ||
      liveMessage.zigbeeRepeaters.find(z => z.deviceId === request.value);

    return !stillExists;
  }

  if (request.settingName === 'setHeatCurveValue') {
    return inf.heatCurveNumber === request.value.curve;
  }


  return false;
}

export default (state = initalState, action) => {
  // split the type value off
  const { type, ...actionData } = action;

  switch (type) {
    case actionTypes.INTERFACE_SETTINGS_UPDATE_STARTED:
    case actionTypes.PRT_SETTINGS_UPDATE_STARTED: {
      // clear the current state, but raise an error if there was one left in progress
      // this probably shouldn't happen but we'd want to know if it did
      const error = state.current
        ? {
            requestId: state.current.request.requestId,
            raised: Date.now(),
            homeId: state.current.request.homeId,
            message: 'Settings update superseded',
          }
        : state.error;

      const current = {
        request: actionData,

        receivedComplete: false,
        receivedMatchingState: false,

        prtSetting: type === actionTypes.PRT_SETTINGS_UPDATE_STARTED,
      };

      return { current, error };
    }

    case actionTypes.WEBSOCKET_LIVE_RECEIVED: {
      if (!state.current) {
        break;
      }

      // we've received an update, but now we've got to test that our change is really in it
      const homeId = actionData.data.home.id;

      if (state.current.request.homeId !== homeId) {
        console.log('Settings-reducer - Live message for a different home', {
          request: state.current.request,
          liveHomeId: homeId,
          newHome: actionData.data.home,
        });
        break;
      }

      const receivedMatchingState = state.current.prtSetting
        ? prtSuccessMatcher(state.current.request, actionData.data.home)
        : interfaceSuccessMatcher(state.current.request, actionData.data.home);

      // check if its resolved
      if (receivedMatchingState && state.current.receivedComplete) {
        return {
          ...state,
          current: null,
        };
      } else {
        // if it's not resolved then we'll just set the new value
        return {
          ...state,
          current: {
            ...state.current,
            receivedMatchingState,
          },
        };
      }
    }

    case actionTypes.CUSTOM_PROPERTIES_UPDATE_COMPLETED:
    case actionTypes.WEBSOCKET_INTERFACE_SETTINGS_UPDATE_RECEIVED:
    case actionTypes.WEBSOCKET_PRT_SETTINGS_UPDATE_RECEIVED: {
      if (!state.current) {
        break;
      }

      const requestId = actionData.data.requestId;

      if (state.current?.request.requestId !== requestId) {
        break;
      }

      // check if its resolved
      if (state.current.receivedMatchingState) {
        return {
          ...state,
          current: null,
        };
      } else {
        // if it's not resolved then we'll just set the new value
        return {
          ...state,
          current: {
            ...state.current,
            // set it complete, unless it was already complete
            receivedComplete: true,
          },
        };
      }
    }

    case actionTypes.WEBSOCKET_INTERFACE_SETTINGS_UPDATE_REJECTED:
    case actionTypes.WEBSOCKET_PRT_SETTINGS_UPDATE_REJECTED:
    case actionTypes.INTERFACE_SETTINGS_UPDATE_TIMED_OUT:
    case actionTypes.PRT_SETTINGS_UPDATE_TIMED_OUT: {
      if (!state.current) {
        break;
      }

      // timeout event has request id at the root, reject has it wrapped in data
      const requestId = actionData.requestId || actionData.data.requestId;

      if (state.current.request.requestId !== requestId) {
        break;
      }

      const message =
        type === actionTypes.WEBSOCKET_PRT_SETTINGS_UPDATE_REJECTED ||
        type === actionTypes.WEBSOCKET_INTERFACE_SETTINGS_UPDATE_REJECTED
          ? 'Settings update rejected'
          : 'Settings update timed out';

      return {
        current: null,
        error: {
          requestId,
          raised: Date.now(),
          deviceId: state.current.deviceId,
          endpointId: state.current.endpointId,
          message,
        },
      };
    }

    case actionTypes.DISMISS_ERROR: {
      const { requestId } = actionData;

      if (state.error?.requestId === requestId) {
        return {
          ...state,
          error: null,
        };
      }
      break;
    }
  }

  return state;
};

export const getPendingError = state => state.error;
